import { stringify } from "../../util/querystring-util";
import { restResourceRequester } from "../rest-resource-requester";
import { GetGeneralCodesRequest } from "./get-general-codes/GetGeneralCodesRequest";
import { GetGeneralCodesResponse } from "./get-general-codes/GetGeneralCodesResponse";
import { GetEdhNotificationTypeListRequest } from "./get-edh-notification-type-list/GetEdhNotificationTypeListRequest";
import { GetEdhNotificationTypeListResponse } from "./get-edh-notification-type-list/GetEdhNotificationTypeListResponse";
import { GetEdhProtocolListRequest } from "./get-edh-protocol-list/GetEdhProtocolListRequest";
import { GetEdhProtocolListResponse } from "./get-edh-protocol-list/GetEdhProtocolListResponse";
import { GetDipsRequest } from "./get-dips/GetDipsRequest";
import { GetDipsResponse } from "./get-dips/GetDipsResponse";
import { GetDipByRegionListRequest } from "./get-dip-by-region-list/GetDipByRegionListRequest";
import { GetDipByRegionListResponse } from "./get-dip-by-region-list/GetDipByRegionListResponse";
import { GetRoleCodeListRequest } from "./get-role-code-list/GetRoleCodeListRequest";
import { GetRoleCodeListResponse } from "./get-role-code-list/GetRoleCodeListResponse";
import { GetTeamCodeListRequest } from "./get-team-code-list/GetTeamCodeListRequest";
import { GetTeamCodeListResponse } from "./get-team-code-list/GetTeamCodeListResponse";
import { GetUsersRequest } from "./get-users/GetUsersRequest";
import { GetUsersResponse } from "./get-users/GetUsersResponse";
import { GetEdiChainTypeListRequest } from "./get-edi-chain-type-list/GetEdiChainTypeListRequest";
import { GetEdiChainTypeListResponse } from "./get-edi-chain-type-list/GetEdiChainTypeListResponse";
import { GetIfStartMethodEdhProtocolListRequest } from "./get-if-start-method-edh-protocol-list/GetIfStartMethodEdhProtocolListRequest";
import { GetIfStartMethodEdhProtocolListResponse } from "./get-if-start-method-edh-protocol-list/GetIfStartMethodEdhProtocolListResponse";
import { GetEdhProtocolFlagUsageListResponse } from "./get-edh-protocol-flag-usage-list/GetEdhProtocolFlagUsageListResponse";
import { GetEdhProtocolFlagUsageListRequest } from "./get-edh-protocol-flag-usage-list/GetEdhProtocolFlagUsageListRequest";
import { GetEdhProtocolCombinationListRequest } from "./get-edh-protocol-combination-list/GetEdhProtocolCombinationListRequest";
import { GetEdhProtocolCombinationListResponse } from "./get-edh-protocol-combination-list/GetEdhProtocolCombinationListResponse";
import { GetLoginUserRoleListRequest } from "./get-login-user-role-list/GetLoginUserRoleListRequest";
import { GetLoginUserRoleListResponse } from "./get-login-user-role-list/GetLoginUserRoleListResponse";
import { GetDipEnvironmentStateManagementListRequest } from "./get-dip-environment-state-management-list/GetDipEnvironmentStateManagementListRequest";
import { GetDipEnvironmentStateManagementListResponse } from "./get-dip-environment-state-management-list/GetDipEnvironmentStateManagementListResponse";
import { GetIfItemListRequest } from "./get-if-item-list/GetIfItemListRequest";
import { GetIfItemListResponse } from "./get-if-item-list/GetIfItemListResponse";
import { get } from "http";
import { GetComponentAttributeListRequest } from "./get-component-attribute-list/GetComponentAttributeListRequest";
import { GetComponentAttributeListResponse } from "./get-component-attribute-list/GetComponentAttributeListResponse";

interface DataListRestResource {
  getGeneralCodes(
    request: GetGeneralCodesRequest,
  ): Promise<GetGeneralCodesResponse>;
  getEdiChainTypeList(
    request: GetEdiChainTypeListRequest,
  ): Promise<GetEdiChainTypeListResponse>;
  getEdhProtocolList(
    request: GetEdhProtocolListRequest,
  ): Promise<GetEdhProtocolListResponse>;
  getEdhNotificationTypeList(
    request: GetEdhNotificationTypeListRequest,
  ): Promise<GetEdhNotificationTypeListResponse>;
  getDips(request: GetDipsRequest): Promise<GetDipsResponse>;
  getDipByRegionList(
    request: GetDipByRegionListRequest,
  ): Promise<GetDipByRegionListResponse>;
  getRoleCodeList(
    request: GetRoleCodeListRequest,
  ): Promise<GetRoleCodeListResponse>;
  getTeamCodeList(
    request: GetTeamCodeListRequest,
  ): Promise<GetTeamCodeListResponse>;
  getUsers(request: GetUsersRequest): Promise<GetUsersResponse>;
  getIfStartMethodEdhProtocolList(
    request: GetIfStartMethodEdhProtocolListRequest,
  ): Promise<GetIfStartMethodEdhProtocolListResponse>;
  getEdhProtocolFlagUsageList(
    request: GetEdhProtocolFlagUsageListRequest,
  ): Promise<GetEdhProtocolFlagUsageListResponse>;
  getEdhProtocolCombinationList(
    request: GetEdhProtocolCombinationListRequest,
  ): Promise<GetEdhProtocolCombinationListResponse>;
  getLoginUserRoleList(
    request: GetLoginUserRoleListRequest,
  ): Promise<GetLoginUserRoleListResponse>;
  getDipEnvironmentStateManagementList(
    request: GetDipEnvironmentStateManagementListRequest,
  ): Promise<GetDipEnvironmentStateManagementListResponse>;
  getIfItemList(request: GetIfItemListRequest): Promise<GetIfItemListResponse>;
  getComponentAttributeList(
    request: GetComponentAttributeListRequest,
  ): Promise<GetComponentAttributeListResponse>;
}

const REST_RESOURCE_NAME = {
  GENERAL_CODES: "/general-codes",
  EDI_CHAIN_TYPE_LIST: "/edi-chain-type-list",
  EDH_PROTOCOL_LIST: "/edh-protocol-list",
  EDH_NOTIFICATION_TYPE_LIST: "/edh-notification-type-list",
  DIPS: "/dips",
  DIP_BY_REGION_LIST: "/dip-by-region-list",
  ROLE_CODE_LIST: "/role-codes",
  TEAM_CODE_LIST: "/team-codes",
  USERS: "/users",
  EDH_PROTOCOL_FLAG_USAGE: "/edh-protocol-flag-usage-list",
  IF_START_METHOD_EDH_PROTOCOL_LIST: "/if-start-method-edh-protocol-list",
  EDH_PROTOCOL_COMBINATION_LIST: "/edh-protocol-combination-list",
  LOGIN_USER_ROLE_LIST: "/login-user-role-list",
  DIP_ENVIRONMENT_STATE_MANAGEMENT_LIST:
    "/dip-environment-state-management-list",
  IF_ITEM_LIST: "/if-item-list",
  COMPONENT_ATTRIBUTE_LIST: "/component-attribute-list",
};

/**
 * GET /general-codes
 */
const getGeneralCodes = restResourceRequester<
  GetGeneralCodesRequest,
  GetGeneralCodesResponse
>("GET", (request) => {
  const queryParameters = {
    cdTypes: request.cdTypes,
  };
  return `${REST_RESOURCE_NAME.GENERAL_CODES}?${stringify(queryParameters)}`;
});

/**
 * GET /edi-chain-type-list
 */
const getEdiChainTypeList = restResourceRequester<
  GetEdiChainTypeListRequest,
  GetEdiChainTypeListResponse
>("GET", (request) => {
  return `${REST_RESOURCE_NAME.EDI_CHAIN_TYPE_LIST}?${stringify(request)}`;
});

/**
 * GET /edh-protocol-list
 */
const getEdhProtocolList = restResourceRequester<
  GetEdhProtocolListRequest,
  GetEdhProtocolListResponse
>("GET", (request) => {
  return `${REST_RESOURCE_NAME.EDH_PROTOCOL_LIST}?${stringify(request)}`;
});

/**
 * GET /edh-notification-type-list
 */
const getEdhNotificationTypeList = restResourceRequester<
  GetEdhNotificationTypeListRequest,
  GetEdhNotificationTypeListResponse
>("GET", (request) => {
  return `${REST_RESOURCE_NAME.EDH_NOTIFICATION_TYPE_LIST}?${stringify(
    request,
  )}`;
});

/**
 * GET /dips
 */
const getDips = restResourceRequester<GetDipsRequest, GetDipsResponse>(
  "GET",
  (_) => {
    return REST_RESOURCE_NAME.DIPS;
  },
);

/**
 * GET /dip-by-region-list
 */
const getDipByRegionList = restResourceRequester<
  GetDipByRegionListRequest,
  GetDipByRegionListResponse
>("GET", (_) => {
  return REST_RESOURCE_NAME.DIP_BY_REGION_LIST;
});

/**
 * GET /role-codes
 */
const getRoleCodeList = restResourceRequester<
  GetRoleCodeListRequest,
  GetRoleCodeListResponse
>("GET", (request) => {
  return `${REST_RESOURCE_NAME.ROLE_CODE_LIST}?${stringify(request)}`;
});

/**
 * GET /team-codes
 */
const getTeamCodeList = restResourceRequester<
  GetTeamCodeListRequest,
  GetTeamCodeListResponse
>("GET", (_) => {
  return REST_RESOURCE_NAME.TEAM_CODE_LIST;
});

/**
 * GET /users
 */
const getUsers = restResourceRequester<GetUsersRequest, GetUsersResponse>(
  "GET",
  (request) => {
    return `${REST_RESOURCE_NAME.USERS}?${stringify(request)}`;
  },
);

/**
 * GET /if-start-method-edh-protocol-list
 */
const getIfStartMethodEdhProtocolList = restResourceRequester<
  GetIfStartMethodEdhProtocolListRequest,
  GetIfStartMethodEdhProtocolListResponse
>("GET", (_) => REST_RESOURCE_NAME.IF_START_METHOD_EDH_PROTOCOL_LIST);

/**
 * GET /edh-protocol-flag-usage-list
 */
const getEdhProtocolFlagUsageList = restResourceRequester<
  GetEdhProtocolFlagUsageListRequest,
  GetEdhProtocolFlagUsageListResponse
>("GET", (_) => REST_RESOURCE_NAME.EDH_PROTOCOL_FLAG_USAGE);

/**
 * GET /edh-protocol-combination-list
 */
const getEdhProtocolCombinationList = restResourceRequester<
  GetEdhProtocolCombinationListRequest,
  GetEdhProtocolCombinationListResponse
>("GET", (_) => REST_RESOURCE_NAME.EDH_PROTOCOL_COMBINATION_LIST);

/**
 * GET /login-user-role-list
 */
const getLoginUserRoleList = restResourceRequester<
  GetLoginUserRoleListRequest,
  GetLoginUserRoleListResponse
>(
  "GET",
  (request) =>
    `${REST_RESOURCE_NAME.LOGIN_USER_ROLE_LIST}?${stringify(request)}`,
);

/*
 * GET /dip-environment-state-management-list
 */
const getDipEnvironmentStateManagementList = restResourceRequester<
  GetDipEnvironmentStateManagementListRequest,
  GetDipEnvironmentStateManagementListResponse
>("GET", (_) => REST_RESOURCE_NAME.DIP_ENVIRONMENT_STATE_MANAGEMENT_LIST);

/**
 * GET /if-item-list
 */
const getIfItemList = restResourceRequester<
  GetIfItemListRequest,
  GetIfItemListResponse
>(
  "GET",
  (request) => `${REST_RESOURCE_NAME.IF_ITEM_LIST}?${stringify(request)}`,
);

/**
 * GET /if-item-list
 */
const getComponentAttributeList = restResourceRequester<
  GetComponentAttributeListRequest,
  GetComponentAttributeListResponse
>(
  "GET",
  (request) =>
    `${REST_RESOURCE_NAME.COMPONENT_ATTRIBUTE_LIST}?${stringify(request)}`,
);

export const dataListRestResource: DataListRestResource = {
  getGeneralCodes: getGeneralCodes,
  getEdiChainTypeList: getEdiChainTypeList,
  getEdhProtocolList: getEdhProtocolList,
  getEdhNotificationTypeList: getEdhNotificationTypeList,
  getDips: getDips,
  getDipByRegionList: getDipByRegionList,
  getRoleCodeList: getRoleCodeList,
  getTeamCodeList: getTeamCodeList,
  getUsers: getUsers,
  getIfStartMethodEdhProtocolList: getIfStartMethodEdhProtocolList,
  getEdhProtocolFlagUsageList: getEdhProtocolFlagUsageList,
  getEdhProtocolCombinationList: getEdhProtocolCombinationList,
  getLoginUserRoleList: getLoginUserRoleList,
  getDipEnvironmentStateManagementList: getDipEnvironmentStateManagementList,
  getIfItemList: getIfItemList,
  getComponentAttributeList: getComponentAttributeList,
};
